import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { promptsApi } from '../api';
import { useToast } from './ToastContext';

/**
 * The user's prompt library.
 *
 * Shared between the composer (pick one), the settings page (edit them) and the
 * batch/compare forms. The selection is remembered per session so a reviewer who
 * always uses "School zone focus" does not re-pick it on every image.
 */

const PromptContext = createContext(null);

export function PromptProvider({ children }) {
  const toast = useToast();
  const [prompts, setPrompts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const list = await promptsApi.list();
      setPrompts(list);
      setSelectedId((current) => {
        if (current && list.some((prompt) => prompt.id === current)) return current;
        return list.find((prompt) => prompt.is_default)?.id ?? null;
      });
      return list;
    } catch (cause) {
      toast.error(cause, { title: 'Could not load prompts' });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (payload) => {
      const prompt = await promptsApi.create(payload);
      await refresh();
      return prompt;
    },
    [refresh],
  );

  const update = useCallback(
    async (id, patch) => {
      const prompt = await promptsApi.update(id, patch);
      await refresh();
      return prompt;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id) => {
      await promptsApi.remove(id);
      setSelectedId((current) => (current === id ? null : current));
      await refresh();
    },
    [refresh],
  );

  const selected = useMemo(
    () => prompts.find((prompt) => prompt.id === selectedId) || null,
    [prompts, selectedId],
  );

  const value = useMemo(
    () => ({
      prompts,
      loading,
      selected,
      selectedId,
      setSelectedId,
      extractionPrompts: prompts.filter((prompt) => prompt.is_extraction),
      chatPrompts: prompts.filter((prompt) => !prompt.is_extraction),
      refresh,
      create,
      update,
      remove,
    }),
    [prompts, loading, selected, selectedId, refresh, create, update, remove],
  );

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>;
}

export function usePrompts() {
  const context = useContext(PromptContext);
  if (!context) throw new Error('usePrompts must be used inside PromptProvider');
  return context;
}
